/**
 * Générateur — Attendu 190 — O8 « Il mène des raisonnements en utilisant des
 * propriétés des figures, des configurations et de la translation. »
 * Niveau : 4ème | Thème #11 Géométrie plane (O8) | Espace et géométrie.
 *
 * ── REVUE 2026-09-24 (vague 4 du chantier « 8 attendus de géométrie 4e ») ──
 * Plafond : Eduscol fin de 4e (2019) l. 407-408 (la ligne elle-même), le
 * protocole l. 389-397 et les exemples l. 428-437 (l'étagère, le
 * parallélisme par des rapports, la nature d'un quadrilatère construit par
 * translations). DEPP début de 2de 2025 q.18 porte cette ligne : « pour
 * montrer que EFG est rectangle en G, il faut utiliser… » : réciproque de
 * Pythagore 63,0 % ; théorème de Pythagore 24,7 % (« confond sens direct et
 * réciproque ») ; réciproque de Thalès 6,5 % ; Thalès 4,8 %. Exercices du
 * prof pour cet attendu : « ED = 1,5 cm donc E'D' = … car … » sur une
 * figure et son image par une translation (ex. 1 et 2).
 *
 * ANCIENNES CARTES (décision prof 24/09 : « les cartes existantes si y en a
 * qui conviennent pas tu supprimes ») :
 *   « Réciproque de Pythagore » : SORTIE, c'est l'automatisme 1089 (« Rectangle
 *     ou pas ? »), qui la reprend sans son indication « Utilise la réciproque ».
 *   « Cas d'égalité des triangles » : SORTIE, absente du programme 2025 ; les
 *     cas d'égalité vivent dans 186, avec une figure.
 *   « Choisir le bon théorème » : RÉÉCRITE en « Quel outil ? », avec une
 *     figure et les données (l'item DEPP q.18), cinq outils.
 *   « Ordonner une démonstration » : gardée et élargie le 24/09, puis SORTIE
 *     le 25/09 (voir la revue ci-dessous).
 *   « Propriété conservée par translation » : RÉÉCRITE en « Pourquoi ? »,
 *     sur la figure et son image, comme l'exercice 1 du prof.
 *
 * ── Revue 2026-09-25 (décisions prof, épreuve de Musk) ───────────────────
 * L'attendu, c'est le moment où l'élève doit trouver LUI-MÊME l'outil et
 * conclure. Donc :
 * 1. « Ordonner une démonstration » (demonstration_ordonner) SORTIE : les
 *    pièces de la démonstration sont données, l'élève les range ; c'est
 *    guidé, l'affaire d'une fiche coup de pouce, pas d'une carte de
 *    l'attendu. Les questions déjà en banque gardent leur contenu.
 * 2. 190 devient une ÉTAGÈRE (mécanisme de gen_1058, détail dans gen_189 :
 *    re-badge obligatoire, clé préfixée par l'outil, nom de carte dans _meta,
 *    calculatrice de la source gardée, solveurs renvoyés dans
 *    tests/solvers/index.js) : en plus de ses cartes, elle réexporte les deux
 *    cartes à réponse JUSTIFIÉE des réciproques, « Droit ou pas ? » (1089,
 *    l'exemple du prof : « est-il rectangle ? Justifier ») et « Parallèles ou
 *    pas ? » (1087, l'exemple Eduscol des rapports). Les cartes d'étapes
 *    (« Les deux calculs », « Les deux rapports », « La conclusion », « Les
 *    étapes dans l'ordre ») et « Rectangle ou pas ? » sans justification
 *    restent dans leurs automatismes.
 *
 * ── Cartes ────────────────────────────────────────────────────────────────
 *   « Quel outil ? » (outil_qcm_unique) : une figure, ses données et un but
 *     (calculer une longueur, un angle, montrer qu'un triangle est rectangle,
 *     que deux droites sont parallèles) ; cinq outils (Pythagore et sa
 *     réciproque, Thalès et sa réciproque, le cosinus), quatre en choix dont
 *     toujours le « miroir » de la clé (direct / réciproque). Le QCM de
 *     l'attendu.
 *   « Pourquoi ? » (pourquoi_texte_trou_drag) : la figure verte et son image
 *     orange (primes), « ED = 1,5 cm donc E'D' = ⬚ car ⬚ » en étiquettes.
 *   « La bonne propriété » (propriete_qcm_unique, ajoutée le 24/09 au soir,
 *     retour du prof : « c'est très lié aux propriétés des figures, aux
 *     configurations et à la translation ») : les PROPRIÉTÉS DES FIGURES qui
 *     manquaient. Une figure, ses données, un but ; « Quelle propriété
 *     permet de conclure ? ». Dix situations : parallélogramme (diagonales
 *     dans les deux sens), rectangle et losange (diagonales, dans les deux
 *     sens), triangle isocèle (angles à la base, dans les deux sens),
 *     translation (le parallélogramme ABDC, Eduscol l. 435-437 ; le
 *     parallélisme, Eduscol l. 355-356). Quatre choix : la clé, sa
 *     réciproque (le piège de la DEPP q.18, ici sur les figures), une
 *     propriété vraie qui ne conclut pas, une propriété fausse. La figure
 *     ne code que les données, jamais le but.
 *   Pas de rappel brouillon ni de calculatrice sur les cartes de 190 (on
 *   raisonne, on ne calcule pas) ; les deux cartes importées gardent la
 *   calculatrice de leur automatisme (on y calcule 6² + 8²).
 *   L'ÉTAGÈRE (cartes importées, voir ETAGERE) :
 *     « Le triangle est-il rectangle ? » (pythagore_droit_qcm_unique, 1089) :
 *       trois longueurs, quatre réponses justifiées, dont « oui, d'après le
 *       théorème de Pythagore » (direct_pour_reciproque, DEPP q.18).
 *     « Les droites sont-elles parallèles ? » (thales_paralleles_qcm_unique,
 *       1087) : quatre longueurs depuis le sommet, réponses justifiées, dont
 *       le piège additif.
 *
 * ── Erreurs (familles nommées) ───────────────────────────────────────────
 *   direct_pour_reciproque (PRINCIPALE, DEPP q.18 24,7 %),
 *   reciproque_pour_direct, thales_pour_pythagore / pythagore_pour_thales
 *   (DEPP q.18 : 6,5 % et 4,8 %), cosinus_pour_longueur ;
 *   conservation_confondue (les angles pour les longueurs),
 *   parallele_pour_longueur (vrai, mais ne justifie pas l'égalité),
 *   mauvaise_mesure (une autre mesure de la figure) ;
 *   conclusion_hors_sujet (une propriété vraie dont l'hypothèse est dans les
 *   données mais qui conclut autre chose), hypothese_absente (une propriété
 *   vraie qui conclut le but, sur une donnée qu'on n'a pas),
 *   propriete_fausse, ordre_des_sommets (ABCD pour ABDC).
 */

import {
    randInt, randomChoice, shuffle, pickLettresSains, tex,
    makeQcm, makeTexteTrou, filtrerDistracteurs,
} from '../../../utils';
import { imgElement } from '../../5eme/statistiques/svgStats';
import { svgTriangle, triangleRectangle, tourner, ORIENTATIONS } from '../../3eme/geometrie-plane/figTriangle';
import { donnee } from './thales';
import { tirerConfiguration as configurationThales } from './gen_1086';
import { generate as genererReciproqueThales, tirerConfiguration as configurationReciproque } from './gen_1087';
import { generate as genererReciproquePythagore } from './gen_1089';
import { quadrilatere, vecteurCoteACote, svgTranslation, svgFleches } from './figTranslation';

const ATTENDU_ID = 190;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_OUTIL = 'Quel outil ?';
const L_POURQUOI = 'Pourquoi ?';
const L_PROPRIETE = 'La bonne propriété';

const POOL = 'ABCDEFGHJKLMNPRSTUV'.split('');
const TRIPLETS = [[3, 4, 5], [6, 8, 10], [9, 12, 15], [12, 16, 20], [8, 15, 17], [7, 24, 25], [20, 21, 29], [15, 20, 25]];
const frDix = (d) => (d % 10 === 0 ? String(d / 10) : `${Math.trunc(d / 10)},${d % 10}`);
const cote = (p, q) => [p, q].sort().join('');

// ═══ Carte « Quel outil ? » (QCM avec figure) ══════════════════════════════

export const OUTILS = {
    pyth: 'Le théorème de Pythagore.',
    recip_pyth: 'La réciproque du théorème de Pythagore.',
    thales: 'Le théorème de Thalès.',
    recip_thales: 'La réciproque du théorème de Thalès.',
    cos: 'Le cosinus.',
};
/** Le miroir de chaque outil : le piège principal (direct ↔ réciproque ; pour le cosinus, Pythagore). */
const MIROIR = { pyth: 'recip_pyth', recip_pyth: 'pyth', thales: 'recip_thales', recip_thales: 'thales', cos: 'pyth' };
const CODE_MIROIR = { pyth: 'reciproque_pour_direct', recip_pyth: 'direct_pour_reciproque', thales: 'reciproque_pour_direct', recip_thales: 'direct_pour_reciproque', cos: 'pythagore_pour_angle' };

/** Un triangle rectangle nommé (R l'angle droit), exact, orienté ; côtés de l'angle droit a et b. */
const triangleOutil = (a, b) => {
    const [R, V, W] = pickLettresSains(POOL, 3);
    const scene = triangleRectangle({ lettres: [R, V, W], droit: 0, a, b, deg: randomChoice(ORIENTATIONS) });
    return { R, V, W, scene, nom: `${R}${V}${W}` };
};
const aire = (t) => `Le triangle ${t.nom}.`;

const SCENARIOS = {
    // trois longueurs, pas de codage : montrer qu'il est rectangle (DEPP q.18)
    recip_pyth: () => {
        const [a, b, c] = randomChoice(TRIPLETS); const t = triangleOutil(a, b);
        const L = { [cote(t.R, t.V)]: a, [cote(t.R, t.W)]: b, [cote(t.V, t.W)]: c };
        const cotes = Object.entries(L).map(([k, v]) => ({ de: k[0], a: k[1], texte: `${v} cm` }));
        const figure = imgElement(svgTriangle({ ...t.scene, angleDroit: null, cotes }, aire(t)), 'large');
        return {
            figure,
            donnees: `Le triangle ${tex(t.nom)} a pour côtés ${Object.entries(L).map(([k, v]) => `${tex(`${k} = ${v}`)} cm`).join(', ')}.`,
            but: `montrer que le triangle ${tex(t.nom)} est rectangle en ${tex(t.R)}`,
        };
    },
    // un triangle rectangle, deux côtés : calculer le troisième
    pyth: () => {
        const [a, b, c] = randomChoice(TRIPLETS); const t = triangleOutil(a, b);
        const cherche = randomChoice([cote(t.V, t.W), cote(t.R, t.V)]);
        const L = { [cote(t.R, t.V)]: a, [cote(t.R, t.W)]: b, [cote(t.V, t.W)]: c };
        const cotes = Object.entries(L).map(([k, v]) => ({ de: k[0], a: k[1], texte: k === cherche ? '?' : `${v} cm`, cherche: k === cherche }));
        const figure = imgElement(svgTriangle({ ...t.scene, cotes }, aire(t)), 'large');
        const donnes = Object.keys(L).filter((k) => k !== cherche);
        return {
            figure,
            donnees: `Le triangle ${tex(t.nom)} est rectangle en ${tex(t.R)}, avec ${donnes.map((k) => `${tex(`${k} = ${L[k]}`)} cm`).join(' et ')}.`,
            but: `calculer ${tex(cherche)}`,
        };
    },
    // un triangle rectangle, le côté adjacent et l'hypoténuse : calculer un angle
    cos: () => {
        const adj = randInt(4, 9); const theta = randomChoice([25, 30, 35, 40, 50, 55, 65]);
        const t = triangleOutil(adj, adj * Math.tan((theta * Math.PI) / 180));
        const hyp = Math.round((adj / Math.cos((theta * Math.PI) / 180)) * 10) / 10;
        const cotes = [{ de: t.R, a: t.V, texte: `${adj} cm` }, { de: t.V, a: t.W, texte: `${String(hyp).replace('.', ',')} cm` }];
        const figure = imgElement(svgTriangle({ ...t.scene, cotes, angles: [{ sommet: t.V, texte: '?', cherche: true }] }, aire(t)), 'large');
        return {
            figure,
            donnees: `Le triangle ${tex(t.nom)} est rectangle en ${tex(t.R)}, avec ${tex(`${cote(t.R, t.V)} = ${adj}`)} cm et ${tex(`${cote(t.V, t.W)} = ${String(hyp).replace('.', '{,}')}`)} cm.`,
            but: `calculer la mesure de l'angle ${tex(`\\widehat{${t.R}${t.V}${t.W}}`)}`,
        };
    },
    // deux triangles emboîtés, les droites parallèles données : calculer une longueur
    thales: () => {
        const c = configurationThales(); const n = c.noms;
        return {
            figure: c.figure,
            donnees: `Les points ${tex(c.L.A)}, ${tex(c.L.M)}, ${tex(c.L.B)} sont alignés, les points ${tex(c.L.A)}, ${tex(c.L.N)}, ${tex(c.L.C)} aussi, et ${tex(`(${n.MN})`)} est parallèle à ${tex(`(${n.BC})`)}. `
                + `On donne ${donnee(n.AM, c.d.AM)}, ${donnee(n.AB, c.d.AB)} et ${donnee(n.BC, c.d.BC)}.`,
            but: `calculer ${tex(n.MN)}`,
        };
    },
    // deux triangles emboîtés, quatre longueurs depuis le sommet : montrer que deux droites sont parallèles
    recip_thales: () => {
        const c = configurationReciproque(true); const n = c.noms;
        return {
            figure: c.figure,
            donnees: `Les points ${tex(c.L.A)}, ${tex(c.L.M)}, ${tex(c.L.B)} sont alignés dans cet ordre, ainsi que les points ${tex(c.L.A)}, ${tex(c.L.N)}, ${tex(c.L.C)}. `
                + `On donne ${donnee(n.AM, c.d.AM)}, ${donnee(n.AB, c.d.AB)}, ${donnee(n.AN, c.d.AN)} et ${donnee(n.AC, c.d.AC)}.`,
            but: `montrer que les droites ${tex(`(${n.MN})`)} et ${tex(`(${n.BC})`)} sont parallèles`,
        };
    },
};

const genOutil = () => {
    const cle = randomChoice(Object.keys(SCENARIOS));
    const s = SCENARIOS[cle]();
    const miroir = MIROIR[cle];
    const autres = shuffle(Object.keys(OUTILS).filter((k) => k !== cle && k !== miroir)).slice(0, 2);
    const texte = `<p>${s.donnees}</p><p>On veut ${s.but}.</p><p><b>Quel outil faut-il utiliser ?</b></p>`;
    const payload = makeQcm([OUTILS[cle], OUTILS[miroir], ...autres.map((k) => OUTILS[k])], OUTILS[cle], texte, { ...opts(L_OUTIL), elements_additionnels: s.figure });
    payload._meta.params = { outil: cle, distracteurs: [{ code: CODE_MIROIR[cle] }, ...autres.map((k) => ({ code: `${k}_pour_${cle}` }))] };
    return payload;
};

// ═══ Carte « Pourquoi ? » (étiquettes, figure et image) ════════════════════

const genPourquoi = () => {
    for (let essai = 0; essai < 50; essai++) {
        const q = quadrilatere();
        const noms = pickLettresSains(POOL, 4); const nomsImage = noms.map((n) => `${n}'`);
        const surLongueur = randInt(0, 1) === 0;
        let phrase; let tuiles; let cotes = []; let angles = [];
        if (surLongueur) {
            const [i, j] = shuffle([0, 1, 2, 3]).slice(0, 2);
            cotes = [{ i, texte: `${frDix(q.cotes[i])} cm` }, { i: j, texte: `${frDix(q.cotes[j])} cm` }];
            const P = noms[i]; const Q = noms[(i + 1) % 4];
            phrase = `${tex(`${P}${Q} = ${frDix(q.cotes[i]).replace(',', '{,}')}`)} cm donc ${tex(`${P}'${Q}' =`)} {{${frDix(q.cotes[i])} cm}} car {{la translation conserve les longueurs}}.`;
            tuiles = [`${frDix(q.cotes[j])} cm`, 'la translation conserve les angles', 'les deux segments sont parallèles'];
        } else {
            const [i, j] = shuffle([0, 1, 2, 3]).slice(0, 2);
            angles = [{ i, texte: `${q.angles[i]}°` }, { i: j, texte: `${q.angles[j]}°` }];
            const P = noms[(i + 3) % 4]; const S = noms[i]; const Q = noms[(i + 1) % 4];
            phrase = `${tex(`\\widehat{${P}${S}${Q}} = ${q.angles[i]}^{\\circ}`)} donc ${tex(`\\widehat{${P}'${S}'${Q}'} =`)} {{${q.angles[i]}°}} car {{la translation conserve les angles}}.`;
            tuiles = [`${q.angles[j]}°`, 'la translation conserve les longueurs', 'la translation conserve les aires'];
        }
        const leurres = [...new Set(filtrerDistracteurs(phrase, tuiles))];
        if (leurres.length < 3) continue;
        const aria = `La figure verte ${noms.join('')} et son image orange ${nomsImage.join('')} par une translation.`;
        const svg = svgTranslation({ poly: q.poly, noms, nomsImage, vecteur: vecteurCoteACote(q.poly), cotes, angles, aria });
        const texte = '<p>La figure orange est l\'image de la figure verte par une translation.</p><p><b>Complète la phrase avec les étiquettes.</b></p>';
        const payload = makeTexteTrou(phrase, texte, { ...opts(L_POURQUOI), mode: 'drag', distracteurs: leurres, elements_additionnels: imgElement(svg, 'large') });
        payload._meta.params = {
            surLongueur,
            distracteurs: surLongueur
                ? [{ code: 'mauvaise_mesure' }, { code: 'conservation_confondue' }, { code: 'parallele_pour_longueur' }]
                : [{ code: 'mauvaise_mesure' }, { code: 'conservation_confondue' }, { code: 'aire_pour_angle' }],
        };
        return payload;
    }
    throw new Error('gen_190 : « Pourquoi ? » sans tirage');
};

// ═══ Carte « La bonne propriété » (QCM avec figure) ════════════════════════

/** Les propriétés, écrites comme au cours (5e : quadrilatères, triangle isocèle ; 4e : translation). Les f_ sont FAUSSES. */
export const PROPRIETES = {
    para_diag: 'Si un quadrilatère est un parallélogramme, alors ses diagonales se coupent en leur milieu.',
    diag_para: 'Si les diagonales d\'un quadrilatère se coupent en leur milieu, alors c\'est un parallélogramme.',
    para_cotes: 'Si un quadrilatère est un parallélogramme, alors ses côtés opposés ont la même longueur.',
    cotes_para: 'Si un quadrilatère a ses côtés opposés parallèles, alors c\'est un parallélogramme.',
    rect_diag: 'Si un quadrilatère est un rectangle, alors ses diagonales ont la même longueur.',
    diag_rect: 'Si un parallélogramme a ses diagonales de même longueur, alors c\'est un rectangle.',
    angles_rect: 'Si un quadrilatère a trois angles droits, alors c\'est un rectangle.',
    los_diag: 'Si un quadrilatère est un losange, alors ses diagonales sont perpendiculaires.',
    diag_los: 'Si un parallélogramme a ses diagonales perpendiculaires, alors c\'est un losange.',
    cotes_los: 'Si un quadrilatère a ses quatre côtés de même longueur, alors c\'est un losange.',
    iso_angles: 'Si un triangle est isocèle, alors ses angles à la base ont la même mesure.',
    angles_iso: 'Si un triangle a deux angles de même mesure, alors il est isocèle.',
    somme: 'La somme des mesures des angles d\'un triangle est égale à 180°.',
    droite_par: 'Si une droite est l\'image d\'une autre droite par une translation, alors ces deux droites sont parallèles.',
    par_droite: 'Si deux droites sont parallèles, alors l\'une est l\'image de l\'autre par une translation.',
    conserve: 'Une translation conserve les longueurs.',
    f_para_diag: 'Si un quadrilatère est un parallélogramme, alors ses diagonales ont la même longueur.',
    f_diag_para: 'Si les diagonales d\'un quadrilatère ont la même longueur, alors c\'est un parallélogramme.',
    f_rect_diag: 'Si un quadrilatère est un rectangle, alors ses diagonales sont perpendiculaires.',
    f_diag_rect: 'Si un parallélogramme a ses diagonales perpendiculaires, alors c\'est un rectangle.',
    f_los_diag: 'Si un quadrilatère est un losange, alors ses diagonales ont la même longueur.',
    f_diag_los: 'Si un parallélogramme a ses diagonales de même longueur, alors c\'est un losange.',
    f_iso: 'Si un triangle est isocèle, alors ses trois angles ont la même mesure.',
    f_angles: 'Si un triangle a deux angles de même mesure, alors il est rectangle.',
    f_droite: 'Si une droite est l\'image d\'une autre droite par une translation, alors ces deux droites sont perpendiculaires.',
};

/** Les quadrilatères, en cm, avant rotation : jamais un rectangle ni un losange « par hasard » (côtés et angles bien différents). */
const QUADRI = {
    parallelogramme: () => { const a = 3.6 + Math.random() * 1.2; const s = 0.9 + Math.random() * 0.8; const h = 2 + Math.random() * 0.6; return [[0, 0], [a, 0], [a + s, h], [s, h]]; },
    rectangle: () => { const a = 3.6 + Math.random() * 1.2; const b = a * (0.5 + Math.random() * 0.2); return [[0, 0], [a, 0], [a, b], [0, b]]; },
    losange: () => { const t = (randInt(55, 68) * Math.PI) / 180; const c = 3 * Math.cos(t); const s = 3 * Math.sin(t); return [[0, 0], [3, 0], [3 + c, s], [c, s]]; },
};

/**
 * Un quadrilatère ABCD (tourné de −12° à 12°), ses diagonales et, si O est
 * demandé, leur point commun ; le nom de O va entre les deux demi-diagonales
 * qui font le plus grand angle (jamais sur un trait) ; droitEnO code l'angle
 * droit en O (entre O B et O C) et le point reste sans nom.
 */
const figureQuadri = ({ forme, L, avecO = false, droitEnO = false, ticks = [], carres = [], aria }) => {
    const { A, B, C, D, O } = L;
    const pts = tourner(QUADRI[forme](), randInt(-12, 12));
    const scene = {
        points: { [A]: pts[0], [B]: pts[1], [C]: pts[2], [D]: pts[3] }, triangle: [A, B, C, D],
        segments: [[A, B], [B, C], [C, D], [D, A], [A, C], [B, D]], ticks, carres,
    };
    if (avecO || droitEnO) {
        const pO = [(pts[0][0] + pts[2][0]) / 2, (pts[0][1] + pts[2][1]) / 2];
        scene.points[O] = pO;
        const u = (P) => { const v = [P[0] - pO[0], P[1] - pO[1]]; const n = Math.hypot(...v); return [v[0] / n, v[1] / n]; };
        const [uA, uB, uC] = [u(pts[0]), u(pts[1]), u(pts[2])];
        const ouverture = (p, q) => Math.acos(Math.max(-1, Math.min(1, p[0] * q[0] + p[1] * q[1])));
        const [p, q] = droitEnO || ouverture(uA, uB) >= ouverture(uB, uC) ? [uA, uB] : [uB, uC];
        scene.directions = { [O]: [p[0] + q[0], p[1] + q[1]] };
        if (droitEnO) { scene.carres = [...carres, { sommet: O, voisins: [B, C] }]; scene.sansEtiquette = [O]; }
    }
    return imgElement(svgTriangle(scene, aria), 'large');
};

/** Un triangle ABC isocèle en A, d'angles à la base `base` degrés ; ticks : AB = AC codé ; mesure : les deux angles à la base écrits. */
const figureIsocele = ({ L, base, ticks = false, mesure = null, aria }) => {
    const { A, B, C } = L;
    const pts = tourner([[0, 2 * Math.tan((base * Math.PI) / 180)], [-2, 0], [2, 0]], randomChoice([0, 0, randInt(-20, 20)]));
    const scene = { points: { [A]: pts[0], [B]: pts[1], [C]: pts[2] }, triangle: [A, B, C], segments: [[A, B], [B, C], [C, A]] };
    if (ticks) scene.ticks = [{ de: A, a: B, n: 1 }, { de: A, a: C, n: 1 }];
    if (mesure) scene.angles = [{ sommet: B, texte: mesure }, { sommet: C, texte: mesure }];
    return imgElement(svgTriangle(scene, aria), 'large');
};

const DIRECT = 'reciproque_pour_direct'; const RECIPROQUE = 'direct_pour_reciproque';

/**
 * Les situations : les données, le but, la figure, et les quatre choix (clé,
 * miroir = sa réciproque, inutile = vraie mais ne conclut pas, fausse).
 * L : les lettres (A, B, C, D, O).
 */
const SITUATIONS = {
    para_diag: (L, n) => ({
        donnees: `${tex(n.ABCD)} est un parallélogramme. Ses diagonales ${tex(`[${L.A}${L.C}]`)} et ${tex(`[${L.B}${L.D}]`)} se coupent en ${tex(L.O)}.`,
        but: `${tex(L.O)} est le milieu de ${tex(`[${L.A}${L.C}]`)}`,
        choix: ['para_diag', 'diag_para', 'para_cotes', 'f_para_diag'], codes: [DIRECT, 'conclusion_hors_sujet', 'propriete_fausse'],
        figure: figureQuadri({ forme: 'parallelogramme', L, avecO: true, aria: `Le parallélogramme ${n.ABCD}, ses diagonales qui se coupent en ${L.O}.` }),
    }),
    diag_para: (L, n) => ({
        donnees: `Les diagonales ${tex(`[${L.A}${L.C}]`)} et ${tex(`[${L.B}${L.D}]`)} du quadrilatère ${tex(n.ABCD)} se coupent en leur milieu ${tex(L.O)}.`,
        but: `${tex(n.ABCD)} est un parallélogramme`,
        choix: ['diag_para', 'para_diag', 'cotes_para', 'f_diag_para'], codes: [RECIPROQUE, 'hypothese_absente', 'propriete_fausse'],
        figure: figureQuadri({
            forme: 'parallelogramme', L, avecO: true,
            ticks: [{ de: L.A, a: L.O, n: 1 }, { de: L.O, a: L.C, n: 1 }, { de: L.B, a: L.O, n: 2 }, { de: L.O, a: L.D, n: 2 }],
            aria: `Le quadrilatère ${n.ABCD}, ses diagonales codées : ${L.O} est le milieu de chacune.`,
        }),
    }),
    rect_diag: (L, n) => ({
        donnees: `${tex(n.ABCD)} est un rectangle.`,
        but: `ses diagonales ${tex(`[${L.A}${L.C}]`)} et ${tex(`[${L.B}${L.D}]`)} ont la même longueur`,
        choix: ['rect_diag', 'diag_rect', 'para_diag', 'f_rect_diag'], codes: [DIRECT, 'conclusion_hors_sujet', 'propriete_fausse'],
        figure: figureQuadri({
            forme: 'rectangle', L,
            carres: [{ sommet: L.A, voisins: [L.D, L.B] }, { sommet: L.B, voisins: [L.A, L.C] }, { sommet: L.C, voisins: [L.B, L.D] }, { sommet: L.D, voisins: [L.C, L.A] }],
            aria: `Le rectangle ${n.ABCD}, ses quatre angles droits codés, et ses diagonales.`,
        }),
    }),
    diag_rect: (L, n) => ({
        donnees: `${tex(n.ABCD)} est un parallélogramme et ses diagonales ${tex(`[${L.A}${L.C}]`)} et ${tex(`[${L.B}${L.D}]`)} ont la même longueur.`,
        but: `${tex(n.ABCD)} est un rectangle`,
        choix: ['diag_rect', 'rect_diag', 'angles_rect', 'f_diag_rect'], codes: [RECIPROQUE, 'hypothese_absente', 'propriete_fausse'],
        figure: figureQuadri({ forme: 'rectangle', L, aria: `Le parallélogramme ${n.ABCD} et ses diagonales, sans codage.` }),
    }),
    los_diag: (L, n) => ({
        donnees: `${tex(n.ABCD)} est un losange.`,
        but: `ses diagonales ${tex(`(${L.A}${L.C})`)} et ${tex(`(${L.B}${L.D})`)} sont perpendiculaires`,
        choix: ['los_diag', 'diag_los', 'para_cotes', 'f_los_diag'], codes: [DIRECT, 'conclusion_hors_sujet', 'propriete_fausse'],
        figure: figureQuadri({
            forme: 'losange', L,
            ticks: [{ de: L.A, a: L.B, n: 1 }, { de: L.B, a: L.C, n: 1 }, { de: L.C, a: L.D, n: 1 }, { de: L.D, a: L.A, n: 1 }],
            aria: `Le losange ${n.ABCD}, ses quatre côtés codés égaux, et ses diagonales.`,
        }),
    }),
    diag_los: (L, n) => ({
        donnees: `${tex(n.ABCD)} est un parallélogramme et ses diagonales ${tex(`(${L.A}${L.C})`)} et ${tex(`(${L.B}${L.D})`)} sont perpendiculaires.`,
        but: `${tex(n.ABCD)} est un losange`,
        choix: ['diag_los', 'los_diag', 'cotes_los', 'f_diag_los'], codes: [RECIPROQUE, 'hypothese_absente', 'propriete_fausse'],
        figure: figureQuadri({ forme: 'losange', L, droitEnO: true, aria: `Le parallélogramme ${n.ABCD} et ses diagonales, l'angle droit codé là où elles se coupent.` }),
    }),
    iso_angles: (L, n) => ({
        donnees: `Le triangle ${tex(n.ABC)} est isocèle en ${tex(L.A)}.`,
        but: `les angles ${tex(`\\widehat{${L.A}${L.B}${L.C}}`)} et ${tex(`\\widehat{${L.A}${L.C}${L.B}}`)} ont la même mesure`,
        choix: ['iso_angles', 'angles_iso', 'somme', 'f_iso'], codes: [DIRECT, 'conclusion_hors_sujet', 'propriete_fausse'],
        figure: figureIsocele({ L, base: randomChoice([40, 50, 70, 75]), ticks: true, aria: `Le triangle ${n.ABC}, les côtés [${L.A}${L.B}] et [${L.A}${L.C}] codés égaux.` }),
    }),
    angles_iso: (L, n) => {
        const v = randomChoice([35, 40, 50, 55, 65, 70, 75]);
        return {
            donnees: `Dans le triangle ${tex(n.ABC)}, les angles ${tex(`\\widehat{${L.A}${L.B}${L.C}}`)} et ${tex(`\\widehat{${L.A}${L.C}${L.B}}`)} mesurent tous les deux ${v}°.`,
            but: `le triangle ${tex(n.ABC)} est isocèle en ${tex(L.A)}`,
            choix: ['angles_iso', 'iso_angles', 'somme', 'f_angles'], codes: [RECIPROQUE, 'conclusion_hors_sujet', 'propriete_fausse'],
            figure: figureIsocele({ L, base: v, mesure: `${v}°`, aria: `Le triangle ${n.ABC}, les angles en ${L.B} et en ${L.C} marqués ${v}°.` }),
        };
    },
    translation_para: (L, n) => {
        const { A, B, C, D } = L;
        const v = [3 + Math.random() * 1.5, -1 + Math.random() * 2.2];
        const cote = randomChoice([1, -1]);
        const c = [0.4 + Math.random() * 1.4, cote * (2 + Math.random() * 0.8)];
        const points = { [A]: [0, 0], [B]: v, [C]: c, [D]: [c[0] + v[0], c[1] + v[1]] };
        const t = `la translation qui transforme ${tex(A)} en ${tex(B)} transforme ${tex(C)} en ${tex(D)}`;
        return {
            donnees: `${t.charAt(0).toUpperCase()}${t.slice(1)}.`,
            but: `${tex(`${A}${B}${D}${C}`)} est un parallélogramme`,
            choix: [`Si ${t}, alors ${tex(`${A}${B}${D}${C}`)} est un parallélogramme.`, `Si ${tex(`${A}${B}${D}${C}`)} est un parallélogramme, alors ${t}.`, PROPRIETES.conserve, `Si ${t}, alors ${tex(n.ABCD)} est un parallélogramme.`],
            codes: [DIRECT, 'conclusion_hors_sujet', 'ordre_des_sommets'],
            figure: imgElement(svgFleches({ points, fleches: [[A, B], [C, D]], aria: `Les points ${A}, ${B}, ${C}, ${D} ; une flèche de ${A} vers ${B} et une flèche de ${C} vers ${D}, de même longueur et de même direction.` }), 'large'),
        };
    },
    droite_par: (L) => {
        const A = L.A; const B = L.B; const A2 = `${A}'`; const B2 = `${B}'`;
        const t = (randInt(-30, 30) * Math.PI) / 180; const w = 2.6 + Math.random() * 0.8;
        const t2 = t + (randomChoice([1, -1]) * randInt(50, 80) * Math.PI) / 180; const d = 1.8 + Math.random() * 0.6;
        const pB = [w * Math.cos(t), w * Math.sin(t)]; const v = [d * Math.cos(t2), d * Math.sin(t2)];
        const points = { [A]: [0, 0], [B]: pB, [A2]: v, [B2]: [pB[0] + v[0], pB[1] + v[1]] };
        return {
            donnees: `La translation qui transforme ${tex(A)} en ${tex(A2)} transforme la droite ${tex(`(${A}${B})`)} en la droite ${tex(`(${A2}${B2})`)}.`,
            but: `les droites ${tex(`(${A}${B})`)} et ${tex(`(${A2}${B2})`)} sont parallèles`,
            choix: ['droite_par', 'par_droite', 'conserve', 'f_droite'], codes: [DIRECT, 'conclusion_hors_sujet', 'propriete_fausse'],
            figure: imgElement(svgFleches({ points, fleches: [[A, A2]], droites: [[A, B], [A2, B2]], aria: `La droite (${A}${B}), la droite (${A2}${B2}) et une flèche de ${A} vers ${A2}.` }), 'large'),
        };
    },
};

const genPropriete = () => {
    const cle = randomChoice(Object.keys(SITUATIONS));
    const [A, B, C, D, O] = pickLettresSains(POOL, 5);
    const L = { A, B, C, D, O };
    const s = SITUATIONS[cle](L, { ABCD: `${A}${B}${C}${D}`, ABC: `${A}${B}${C}` });
    const choix = s.choix.map((k) => PROPRIETES[k] || k);
    const texte = `<p>${s.donnees}</p><p>On veut montrer que ${s.but}.</p><p><b>Quelle propriété permet de conclure ?</b></p>`;
    const payload = makeQcm(choix, choix[0], texte, { ...opts(L_PROPRIETE), elements_additionnels: s.figure });
    payload._meta.params = { situation: cle, distracteurs: s.codes.map((code) => ({ code })) };
    return payload;
};

// ═══ Contrat distracteurs (référence de revue) ═════════════════════════════

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: ['figures exactes (décision prof 24/09) ; lettres saines en KaTeX ; aucun calcul demandé'],
    outil_qcm_unique: 'quatre des cinq outils : la clé, son miroir (direct_pour_reciproque ou reciproque_pour_direct, PRINCIPALE, DEPP q.18 24,7 % ; pour le cosinus, Pythagore) et deux autres tirés',
    propriete_qcm_unique: 'la clé, sa réciproque (direct_pour_reciproque ou reciproque_pour_direct, PRINCIPALE), une propriété vraie qui ne conclut pas (conclusion_hors_sujet ou hypothese_absente), une propriété fausse (propriete_fausse ; ordre_des_sommets pour la translation) ; la figure ne code que les données',
    etagere: 'les QCM importés gardent le contrat de leur gen : droit_qcm_unique (1089), paralleles_qcm_unique (1087)',
};

// ═══ L'étagère : les cartes importées des automatismes (revue 2026-09-25) ═══

/** Clé préfixée par l'outil → le gen source, sa clé, le nom de carte (sans l'outil : le prof le voit, l'élève doit le trouver) et le format affiché. */
const ETAGERE = {
    pythagore_droit_qcm_unique: { generer: genererReciproquePythagore, cle: 'droit_qcm_unique', label: 'Le triangle est-il rectangle ?', format: 'QCM' },
    thales_paralleles_qcm_unique: { generer: genererReciproqueThales, cle: 'paralleles_qcm_unique', label: 'Les droites sont-elles parallèles ?', format: 'QCM' },
};

/** Le payload de la source, re-badgé 190 : l'attendu, la clé préfixée et le nom de carte ; tout le reste est celui de la source. */
const genererEtagere = (key) => {
    const e = ETAGERE[key];
    const payload = e.generer(e.cle);
    if (payload._meta.generatorKey !== e.cle) throw new Error(`gen_190 étagère : ${key} n'a pas rendu ${e.cle}`);
    payload.attendu = ATTENDU_ID;
    payload._meta.generatorKey = key;
    payload._meta.label = e.label;
    return payload;
};

// ═══ Registre et point d'entrée ════════════════════════════════════════════

const GENERATORS = {
    outil_qcm_unique: genOutil,
    pourquoi_texte_trou_drag: genPourquoi,
    propriete_qcm_unique: genPropriete,
};

const CLES = [...Object.keys(GENERATORS), ...Object.keys(ETAGERE)];

export const CATEGORIES = [
    { id: 'outil', label: L_OUTIL, formatsList: [{ key: 'outil_qcm_unique', label: 'QCM' }] },
    { id: 'propriete', label: L_PROPRIETE, formatsList: [{ key: 'propriete_qcm_unique', label: 'QCM' }] },
    { id: 'pourquoi', label: L_POURQUOI, formatsList: [{ key: 'pourquoi_texte_trou_drag', label: 'Étiquettes' }] },
    ...Object.entries(ETAGERE).map(([key, e]) => ({ id: key, label: e.label, formatsList: [{ key, label: e.format }] })),
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && CLES.includes(typeExercice)) key = typeExercice;
    else if (typeExercice) {
        const matching = CLES.filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(CLES);
    } else key = randomChoice(CLES);
    if (ETAGERE[key]) return genererEtagere(key);
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = { ETAGERE };
